import { useTranslation } from 'react-i18next';
import {
  useSavedFilterViews,
  type SavedFilterView,
  type SavedFilterViews,
} from '~/hooks/useSavedFilterViews';
import { preferenceSlot } from '~/hooks/usePreferenceWriteQueue';
import { savedViewMessages } from '~/utils/savedViewMessages';
import { isComparisonFilterData } from '~/types';
import type { GetUserPreferencesQuery } from '~/graphql/graphql';
import type { ComparisonFilterRow } from '~/pages/order-builder/lib/comparisonFilters';
import {
  readComparisonFilterRows,
  rowsToFilterData,
} from '~/pages/order-builder/lib/comparisonFilterStorage';

const COMPARISON_VIEW = 'COMPARISON' as const;

const COMPARISON_PERSPECTIVE = 'BUYING' as const;

type Preferences = GetUserPreferencesQuery['userPreferences'] | undefined;

export type ComparisonSavedView = SavedFilterView;
export type ComparisonSavedViews = SavedFilterViews<ComparisonFilterRow>;

function activeIdOf(preferences: Preferences): string | null {
  return preferences?.buyerComparisonActiveFilterId ?? null;
}

function anonymousOf(preferences: Preferences): unknown {
  return preferences?.buyerComparisonAnonymousFilter;
}

export function useComparisonSavedViews(): ComparisonSavedViews {
  const { t } = useTranslation('order-builder');

  return useSavedFilterViews<ComparisonFilterRow>({
    view: COMPARISON_VIEW,
    perspective: COMPARISON_PERSPECTIVE,
    slot: preferenceSlot('comparison', COMPARISON_PERSPECTIVE),
    isOwnFilterData: isComparisonFilterData,
    readRows: readComparisonFilterRows,
    rowsToFilterData,
    activeIdOf,
    anonymousOf,
    anonymousPreferences: (rows) => ({
      buyerComparisonAnonymousFilter: rowsToFilterData(rows),
    }),
    holdHydrationWhileWriting: true,
    messages: savedViewMessages(t, 'order_builder.filter'),
  });
}
